// @ts-strict-ignore
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';

import {CardBackgroundRepollState} from 'apps/boards/src/dashboards-app/types';
import {AppMode} from 'apps/boards/src/models/enums';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {
  BenchmarkCard,
  Bookmark,
  BookmarkParamsUnionType,
  CardOnDashboard,
  ContentType,
  Dashboard,
  DashboardCard,
  DashboardCardChartOptions,
  DisplayBookmark,
  MediaCard,
  NestedDashboardCard,
  TextCard,
  TimeFilter,
} from 'common/report/dashboards/types';
import type {DashboardCardResult} from 'apps/boards/src/report-util/query';
import {INVALID_BOOKMARK_ERROR} from 'common/report/controllers/dashboard-model-controller';
import {CurrentProject} from 'common/report/model-resources/projects';
import {ReportManager} from 'common/report/report-manager';
import {confirm} from 'common/components/confirm';
import {THEME as ConfirmTheme} from 'common/components/confirm/types';
import {generateStagedCardId} from 'common/report/dashboards/url';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {hasSavedBehavior, hasSavedFormula} from 'common/report/insights/models/utils';
import {isInsightsBookmarkParams} from 'common/report/insights/util/types';
import {LIMITED_CONCURRENCY_PROJECTS, PropertyType, VERY_LIMITED_CONCURRENCY_PROJECTS} from 'common/report/constants';
import {BoardFilter, PersistedBoardFilter} from 'apps/boards/src/dashboards-app/components/global-filter-bar/types';
import {StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {Segmentation} from 'common/types/reports/query';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {ReportStatus} from 'common/widgets/warning-hero/constants';
import {DashboardCardId} from 'common/report/util/saved-entities/types';

const DEFAULT_QUERY_CONCURRENCY = 10;
const DEFAULT_BACKGROUND_QUERY_CONCURRENCY = Math.ceil(DEFAULT_QUERY_CONCURRENCY / 2);
const FILTER_COMPARISON_PROPS: Array<keyof BoardFilter> = [
  `filterValue`,
  `customPropertyId`,
  `resourceType`,
  `propertyDefaultType`,
  `propertyName`,
  `propertyType`,
  `joinPropertyType`,
  `propertyObjectKey`,
  `filterOperator`,
  `scdEvent`,
  `scdOptions`,
  `limitValues`,
  `defaultEmpty`,
];
const MAX_CARDS_TO_LOAD_IMMEDIATELY = 10;
const MIN_QUERY_CONCURRENCY = 5;
const MIN_BACKGROUND_QUERY_CONCURRENCY = Math.ceil(MIN_QUERY_CONCURRENCY / 2);

type GetBaseCardParamsOptions = {
  canEdit?: boolean;
  disableHeaderHoverAnimation?: boolean;
  isAutofocused?: boolean;
  isHighlighted?: boolean;
  isPublicMode?: boolean;
};

const getBaseCardParams = (card, options) => {
  return {
    canEdit: options.hasEditPermissions,
    cardId: card.id,
    disableHeaderHoverAnimation: options.isAnyCardBeingDragged,
    isAutofocused: options.isAutofocused,
    isHighlighted: options.isHighlighted,
    isPublicMode: options.isPublicMode,
  };
};

export type GetDashboardCardParamsOptions = GetBaseCardParamsOptions & {
  chartOptions: DashboardCardChartOptions;
  dataGroups: DataGroup[];
  enableCustomAlerts: boolean;
  hasEditPermissions: boolean;
  isEmbedMode: boolean;
  isDefaultChartType?: boolean;
  isLinked?: boolean;
  isAnyCardBeingDragged?: boolean;
  dashboardMode: AppMode;
  isTvMode: boolean;
  projectId: number;
  projectTimeZone: string;
  projectAsOfDate: string;
  reportUrl: string;
  timeFilter: string; // JSON.stringified time filter
  UTCOffset: number;
};

/**
 * Used in both standard-mode and public-mode for consistent behaviors in both dashboard mode
 *
 * @param card
 * @param cardResult
 * @param options
 * @param cardRepollState
 */
export const getDashboardCardParams = (
  card: DashboardCard,
  cardResult: DashboardCardResult,
  options: GetDashboardCardParamsOptions,
  cardRepollState: CardBackgroundRepollState = CardBackgroundRepollState.None,
) => {
  const baseParams = getBaseCardParams(card, options);
  const reportIsInErrorState: boolean =
    !!cardResult.chartError || card.bookmark?.error === INVALID_BOOKMARK_ERROR || cardResult.invalidBookmark;
  const reportLoading: boolean = !(
    cardResult.chartLoaded ||
    !!cardResult.chartError ||
    cardResult.invalidBookmark ||
    cardResult.notSupported ||
    cardResult.dataGroupNotSupported
  );
  const reportHasNoData: boolean = !reportIsInErrorState && !cardResult.chartParams;
  const reportStatus: ReportStatus = reportLoading
    ? ReportStatus.Loading
    : reportIsInErrorState
      ? ReportStatus.Error
      : reportHasNoData
        ? ReportStatus.NoData
        : ReportStatus.Success;

  const params = {
    ...baseParams,
    cardTitle: card.bookmark.name,
    chartOptions: options.chartOptions,
    fromDate: cardResult.fromDate || ``,
    hideResetChartTypeOption: options.isDefaultChartType || !options.isLinked,
    isDigestMode: options.dashboardMode === AppMode.Digest,
    isEmbedMode: options.isEmbedMode,
    isLinked: options.isLinked,
    isTextCard: false,
    isTvMode: options.isTvMode,
    lastUpdated: cardResult.queryLastUpdated || ``,
    layoutCardType: card.type || ContentType.ReportLink,
    projectId: options.projectId,
    projectTimezone: options.projectTimeZone,
    projectAsOfDate: options.projectAsOfDate || ``,
    reportRepollState: cardRepollState,
    reportStatus,
    reportError: cardResult.chartError,
    reportLoading,
    reportBookmarkMissing: card.bookmark?.error === INVALID_BOOKMARK_ERROR,
    reportDataGroupNotSupported: cardResult.dataGroupNotSupported,
    reportDataGroupNotSupportedReason: cardResult.dataGroupNotSupportedReason,
    reportBookmarkInvalid: cardResult.invalidBookmark,
    reportLoadingFailed:
      !!cardResult.chartError ||
      cardResult.invalidBookmark ||
      cardResult.notSupported ||
      cardResult.dataGroupNotSupported,
    reportName: card.bookmark.type,
    reportNotSupported: cardResult.notSupported,
    reportUrl: options.reportUrl,
    timeFilter: JSON.parse(options.timeFilter) as TimeFilter,
    toDate: cardResult.toDate || ``,
    utcOffset: options.UTCOffset,
  };

  return params;
};

export type GetTextCardParamsOptions = GetBaseCardParamsOptions & {
  isPublicMode: boolean;
  hasEditPermissions: boolean;
  isTvMode: boolean;
};

/**
 * Used in both standard-mode and public-mode for consistent behaviors in both dashboard mode
 *
 * @param card
 * @param options
 */
export const getTextCardParams = (card: TextCard, options: GetTextCardParamsOptions) => {
  const baseParams = getBaseCardParams(card, options);
  const params = {
    ...baseParams,
    isTextCard: true,
    layoutCardType: card.type || ContentType.Text,
    markdown: card.markdown,
  };
  return params;
};

/**
 * Used in both standard-mode and public-mode for consistent behaviors in both dashboard mode
 *
 * @param card
 * @param options
 */
export const getBenchmarkCardParams = (
  card: BenchmarkCard,
  cardResult: DashboardCardResult,
  options: GetTextCardParamsOptions,
) => {
  const baseParams = getBaseCardParams(card, options);
  const params = {
    ...baseParams,
    layoutCardType: card.type || ContentType.Benchmark,
    benchmarkType: card.benchmark_type,
    industry: card.industry,
    reportLoading: !(
      cardResult.chartLoaded ||
      !!cardResult.chartError ||
      cardResult.invalidBookmark ||
      cardResult.notSupported ||
      cardResult.dataGroupNotSupported
    ),
  };
  return params;
};

type GetNestedDashboardCardParamsOptions = GetBaseCardParamsOptions & {
  reportUrl: string;
  currentDashboardHierarchyTreeHeight: number;
  descendantDashboardIds: Set<number>;
  isCurrentDashboardNested: boolean;
};

export const getNestedDashboardCardParams = (
  card: NestedDashboardCard,
  options: GetNestedDashboardCardParamsOptions,
) => {
  const baseParams = getBaseCardParams(card, options);
  const params = {
    ...baseParams,
    isTextCard: true,
    layoutCardType: card.type || ContentType.Dashboard,
    thumbnailLayout: card.thumbnail_layout,
    thumbnailContents: card.thumbnail_contents,
    cardTitle: card.title,
    reportUrl: options.reportUrl,
    lastUpdated: card.last_edited || ``,
  };
  return params;
};

/**
 * Determines the number of cards to autoload as soon as the dashboard is loaded.
 *
 * @param {String} projectId the project id of this dashboard, may be unset due to page loading.
 * @returns the number of cards to load immediately.
 */
export function getMaxCardsToLoadImmediately(projectId?: string): number {
  // Explicitly limit card autoloading of certain very large and large projects.
  if (VERY_LIMITED_CONCURRENCY_PROJECTS.has(projectId) || LIMITED_CONCURRENCY_PROJECTS.has(projectId)) {
    return 1;
  }
  return MAX_CARDS_TO_LOAD_IMMEDIATELY;
}

/**
 * Determines the maximum number of concurrent queries allowed to query API on a dashboard.
 *
 * For certain projects that we know can cause latency issues we apply project-specific
 * limiting in two buckets, very limited (concurrency: 2) or limited (concurrency: 5).
 *
 * In other cases we tweak the concurrency based on the number of dashboard cards. The idea being
 * that we want small dashboards to be able to take advantage of higher concurrency to load
 * quickly and on larger dashboards we want to limit concurrency to throttle the load that is put
 * on arb by the high number of queries. In these cases we max out at concurrency 10 and bottom
 * out at concurrency 5.
 *
 * @param {String} projectId the project id of this dashboard, may be unset due to page loading
 *        delays on public dashboards.
 * @param {Dashboard} dashboard the dashboard that we are figuring out concurrency for, may be
 *        unset due to page loading delays.
 * @returns
 */
export function getMaxConcurrentQueries(projectId?: string, dashboard?: Dashboard): number {
  const numCards = dashboard?.cards?.length ?? 0;

  // Explicitly limit the concurrency of certain very large and large projects.
  if (VERY_LIMITED_CONCURRENCY_PROJECTS.has(projectId)) {
    return 2;
  } else if (LIMITED_CONCURRENCY_PROJECTS.has(projectId)) {
    return 5;
  }
  return Math.max(DEFAULT_QUERY_CONCURRENCY - Math.floor(numCards / 60), MIN_QUERY_CONCURRENCY);
}

/**
 * Determines the maximum number of concurrent background low-priority queries allowed to query API
 * on a dashboard.
 *
 * For certain projects that we know can cause latency issues we apply project-specific
 * limiting of 1 background query at a time.
 *
 * In other cases we tweak the concurrency based on the number of dashboard cards. The idea being
 * that we want small dashboards to be able to take advantage of higher concurrency to load
 * quickly and on larger dashboards we want to limit concurrency to throttle the load that is put
 * on arb by the high number of queries. In these cases we max out at concurrency 5 and bottom
 * out at concurrency 3.
 *
 * @param {String} projectId the project id of this dashboard, may be unset due to page loading
 *        delays on public dashboards.
 * @param {Dashboard} dashboard the dashboard that we are figuring out concurrency for, may be
 *        unset due to page loading delays.
 * @returns
 */
export function getMaxConcurrentBackgroundPoolQueries(projectId?: string, dashboard?: Dashboard): number {
  const numCards = dashboard?.cards?.length ?? 0;

  // Explicitly limit the concurrency of certain very large and large projects.
  if (VERY_LIMITED_CONCURRENCY_PROJECTS.has(projectId) || LIMITED_CONCURRENCY_PROJECTS.has(projectId)) {
    return 1;
  }

  return Math.max(DEFAULT_BACKGROUND_QUERY_CONCURRENCY - Math.floor(numCards / 10), MIN_BACKGROUND_QUERY_CONCURRENCY);
}

/**
 * Used in both standard-mode and public-mode for consistent behaviors in both dashboard mode
 *
 * @param card
 * @param options
 */
export const getMediaCardParams = (card: MediaCard, options: GetTextCardParamsOptions) => {
  const baseParams = getBaseCardParams(card, options);
  const params = {
    ...baseParams,
    // TODO pjboudrx: refactor is-text-card to be more general to support media cards
    isTextCard: true,
    layoutCardType: card.type || ContentType.Media,
    mediaType: card.media_type,
    mediaService: card.service,
    mediaPath: card.path,
  };
  return params;
};

export const NULL_BOOKMARK: Bookmark = {
  creator_id: null,
  creator_name: null,
  creator_email: null,
  dashboard: null,
  id: null,
  is_default: false,
  name: ``,
  original_type: null,
  params: null,
  project_id: null,
  workspace_id: null,
  type: null,
};

export function createStagedBookmark<BookmarkParamsType extends BookmarkParamsUnionType>(
  project: CurrentProject,
  reportManager: ReportManager<BookmarkParamsType>,
): DisplayBookmark<BookmarkParamsType> {
  const params = reportManager.getDefaultBookmarkParams(project);

  return {
    name: ``,
    description: ``,
    params,
    type: reportManager.insightsReportMode || reportManager.bookmarkType,
  };
}

export function isBenchmarkCard(card: CardOnDashboard): card is BenchmarkCard {
  return card.type === ContentType.Benchmark;
}

export function isTextOrMediaCard(card: CardOnDashboard) {
  return card.type === ContentType.Text || card.type === ContentType.Media;
}

export function confirmDeleteCard(): Promise<boolean> {
  return confirm({
    title: `Delete`,
    description: `Deleting is permanent. Doing so will not delete any related cards. Are you sure you want to delete?`,
    theme: ConfirmTheme.DELETE,
    'confirm-button-label': `Delete`,
  });
}

export function createStagedCard<BookmarkParamsType extends BookmarkParamsUnionType>(
  bookmarkType: BookmarkType,
  bookmark: Bookmark<BookmarkParamsType>,
): DashboardCard<BookmarkParamsType> {
  return {
    id: generateStagedCardId(bookmarkType),
    width: 1,
    height: 1,
    type: ContentType.Report,
    bookmark,
  };
}

export function isEqualToSavedFilters(oldFilters: Nullable<BoardFilter[]>, newFilters: Nullable<BoardFilter[]>) {
  return isEqual(
    (newFilters || []).map((filter) => pick(filter, FILTER_COMPARISON_PROPS)),
    (oldFilters || []).map((filter) => pick(filter, FILTER_COMPARISON_PROPS)),
  );
}

export function isEqualToSavedBreakdowns(
  oldBreakdowns: Nullable<Array<Segment | Segmentation>>,
  newBreakdowns: Nullable<Array<Segment | Segmentation>>,
) {
  return isEqual(newBreakdowns, oldBreakdowns);
}

/** Await resource fetches if the bookmark has saved formulas or behaviors. Note this is very similar to InsightsReportManager#waitForApiResources */
export async function waitForNecessaryResources({
  metricsResource,
  behaviorsResource,
  bookmark,
}: {
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  bookmark: Bookmark;
}) {
  if (isInsightsBookmarkParams(bookmark.params)) {
    if (hasSavedFormula(bookmark.params)) {
      await metricsResource.waitForIndexFetch();
    }
    if (hasSavedBehavior(bookmark.params)) {
      await behaviorsResource.waitForIndexFetch();
    }
  }
}

/** Checks if a board filter should have a inline value dropdown. */
export function doesBoardFilterHaveDropdown(boardFilter: Segmentation): boolean {
  // String "is" / "is not" should always show dropdown
  return (
    boardFilter?.propertyType === PropertyType.String &&
    (boardFilter.filterOperator === StringOperator.Equals || boardFilter.filterOperator === StringOperator.DoesNotEqual)
  );
}

/** Initializes UI board filters from a dashboard model. */
export function getBoardFiltersFromPersisted(persistedBoardFilters: PersistedBoardFilter[]): BoardFilter[] {
  return persistedBoardFilters.map((filter) => {
    // if we aren't defaulting to empty, make the filterValue active.
    const defaultActiveValue = filter.defaultEmpty ? undefined : filter.filterValue;

    // if data comes in from the url, possible that we have an activeValue. we want to preserve it if that's the case
    const initialActiveValue = `activeValue` in filter ? filter.activeValue : defaultActiveValue;

    return {
      ...filter,
      activeValue: doesBoardFilterHaveDropdown(filter) ? initialActiveValue : undefined,
    };
  });
}

/** Creates PropertyFilters from BoardFilters */
export function getActiveBoardFilters(boardFilters: BoardFilter[]): PropertyFilter[] {
  return boardFilters
    .filter((boardFilter) => {
      // we won't apply any pinned filters that don't have a value selected
      const isEmptyPinned = doesBoardFilterHaveDropdown(boardFilter) && !boardFilter.activeValue;

      return !isEmptyPinned;
    })
    .map((boardFilter) => {
      const {activeValue, ...attrs} = boardFilter;

      return new PropertyFilter({
        ...attrs,
        // for pinned filters, we use the active value rather than the filter definition's value
        filterValue: doesBoardFilterHaveDropdown(boardFilter) ? activeValue : boardFilter.filterValue,
      });
    });
}

export function getCardQuerySpanId(cardId: DashboardCardId): string {
  return `report-query:${cardId}`;
}

export function getCardLoadingSpanId(cardId: DashboardCardId): string {
  return `card.loading:${cardId}`;
}
